import { useState, useCallback } from 'react'
import type { CutleryType } from './CutlerySVGs'
import type { Difficulty } from './useCutlery'
import type { ComandaData, OrderItem } from './tableData'
import { generateComanda } from './tableData'

export type { OrderItem, ComandaData }

const ALL_TYPES: CutleryType[] = ['spoon', 'fork', 'knife', 'teaspoon']

function emptyCount(): Record<CutleryType, number> {
  return { spoon: 0, fork: 0, knife: 0, teaspoon: 0 }
}

export interface CountState {
  dinerCount:  number
  order:       OrderItem[]
  minCutlery:  Record<CutleryType, number>   // correct with reuse
  maxCutlery:  Record<CutleryType, number>   // correct without reuse
  extracted:   Record<CutleryType, number>
  checkResult: Record<CutleryType, 'correct' | 'wrong'> | null
  moves:       number
  startTime:   number | null
  endTime:     number | null
  gameComplete: boolean
}

function initState(difficulty: Difficulty): CountState {
  const data: ComandaData = generateComanda(difficulty)
  return {
    dinerCount:  data.dinerCount,
    order:       data.items,
    minCutlery:  data.minCutlery,
    maxCutlery:  data.maxCutlery,
    extracted:   emptyCount(),
    checkResult: null,
    moves:       0,
    startTime:   null,
    endTime:     null,
    gameComplete: false,
  }
}

export function useCountCutlery(difficulty: Difficulty) {
  const [state, setState] = useState<CountState>(() => initState(difficulty))

  const resetGame = useCallback(() => {
    setState(initState(difficulty))
  }, [difficulty])

  const addPiece = useCallback((type: CutleryType) => {
    setState((prev) => {
      if (prev.gameComplete) return prev
      return {
        ...prev,
        extracted:   { ...prev.extracted, [type]: prev.extracted[type] + 1 },
        checkResult: null,
        startTime:   prev.startTime ?? Date.now(),
      }
    })
  }, [])

  const removePiece = useCallback((type: CutleryType) => {
    setState((prev) => {
      if (prev.gameComplete || prev.extracted[type] === 0) return prev
      return {
        ...prev,
        extracted:   { ...prev.extracted, [type]: prev.extracted[type] - 1 },
        checkResult: null,
      }
    })
  }, [])

  const checkAnswer = useCallback(() => {
    setState((prev) => {
      if (prev.gameComplete) return prev
      const checkResult = {} as Record<CutleryType, 'correct' | 'wrong'>
      for (const type of ALL_TYPES) {
        const v = prev.extracted[type]
        // Accept any count in [min, max] — both reuse and no-reuse are valid
        checkResult[type] = v >= prev.minCutlery[type] && v <= prev.maxCutlery[type]
          ? 'correct'
          : 'wrong'
      }
      const allCorrect = ALL_TYPES.every((t) => checkResult[t] === 'correct')
      const moves = prev.moves + 1
      return {
        ...prev,
        checkResult,
        moves,
        gameComplete: allCorrect,
        endTime:      allCorrect ? Date.now() : prev.endTime,
      }
    })
  }, [])

  return { state, resetGame, addPiece, removePiece, checkAnswer }
}
